import { Badge, Button, Flex, Table, Text } from "@radix-ui/themes";
import { useState, useCallback, useMemo } from "react";
import { CompareRegressionTestsDialog } from "@/routes/regression-tests/CompareRegressionTestsDialog";
import { Search } from "@/components/shared/inputs/search";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { useAuth0 } from "@auth0/auth0-react";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { AllRegressionTests } from "@/v2/entities/regression/Regression.model";
import { useQuery } from "react-query";

function isQuackOrTheQuackEmail(email: string | undefined): boolean {
  if (!email) return false;
  const lower = email.toLowerCase();
  return lower.endsWith("@quack.ai") || lower.endsWith("@thequack.ai");
}

export function RegressionTests() {
  const { user } = useAuth0();
  const { getAllRegressionTests, getRegressionSets } = useRegressionApi();
  const auiRegressionTestsEnabled = isQuackOrTheQuackEmail(user?.email);

  const { data: results, isLoading: testsLoading } = useQuery({
    queryKey: ["regression-tests"],
    queryFn: getAllRegressionTests,
  });
  const { data: regressionSets } = useQuery({
    queryKey: ["regression-sets"],
    queryFn: getRegressionSets,
  });

  const [searchPhrase, setSearchPhrase] = useState("");
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const navigate = useNavigate();

  const formatRunMethodLabel = useCallback(
    (runMethod: AllRegressionTests["runMethod"]) => {
      if (runMethod === "manual") return "Manual";
      if (runMethod === "auto") return "Automatic";
      if (runMethod === "aui") return "AUI";
      return "—";
    },
    []
  );

  const sortedRegressionTests = useMemo(() => {
    if (!results) return [];
    return [...results].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [results]);

  const handleAuiRegressionTestsClick = useCallback(() => {
    navigate("/automation-center/aui-regression-tests");
  }, [navigate]);

  const handleCompareDialogOpenChange = useCallback((open: boolean) => {
    setCompareDialogOpen(open);
  }, []);

  const handleOpenCompareDialog = useCallback(() => {
    setCompareDialogOpen(true);
  }, []);

  const isLoading = testsLoading;

  return (
    <Flex direction={"column"} gap={"8"} px={"8"} py={"8"}>
      <Flex direction={"row"} gap={"2"} justify={"between"}>
        <Flex direction={"column"} gap={"2"}>
          <Flex align="center" gap="2">
            <Button
              variant="ghost"
              size="1"
              onClick={() => navigate("/automation-center/regression-sets")}
            >
              <ArrowLeftIcon />
            </Button>
            <h3 style={{ fontWeight: "500" }}>All Regression Tests</h3>
          </Flex>
          <Text size="2" color="gray">
            View all regression test runs and their results. to drill down to a
            specific test, click on the "view results" button.
          </Text>
        </Flex>
        <Flex gap="2" align="center" wrap="wrap" justify="end">
          <Button
            variant="soft"
            size="2"
            color="gray"
            onClick={handleOpenCompareDialog}
          >
            Compare tests results
          </Button>
          {auiRegressionTestsEnabled && (
            <Button
              variant="soft"
              size="2"
              onClick={handleAuiRegressionTestsClick}
            >
              AUI Regression Tests
            </Button>
          )}
        </Flex>
      </Flex>

      <CompareRegressionTestsDialog
        open={compareDialogOpen}
        onOpenChange={handleCompareDialogOpenChange}
        regressionSets={regressionSets}
      />

      <Flex direction={"column"} gap={"2"}>
        <Flex direction={"row"} gap={"2"}>
          <Flex gap={"2"} align={"center"}>
            <Text size={"3"} weight={"medium"}>
              Test Results
            </Text>
            <Badge color="gray">{results?.length || 0}</Badge>
          </Flex>
          <Search
            initialValue={searchPhrase}
            placeholder="Search test results"
            setSearchPhrase={setSearchPhrase}
            debounce={50}
          />
        </Flex>

        <Table.Root>
          <Table.Header>
            <Table.Row className=" text-gray-500 font-normal">
              <Table.ColumnHeaderCell className=" font-normal w-1/6">
                Test ID
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/4">
                Regression Set
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/6">
                Run method
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/4">
                Number of questions
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/4">
                Results
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/4">
                Rank
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/4">
                Created At
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/6">
                Result
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/6"></Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading && (
              <Table.Row>
                <Table.Cell colSpan={9}>
                  <Flex align="center" justify="center" py="6">
                    <Text size="2" color="gray">
                      Loading...
                    </Text>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            )}
            {!isLoading && (!results || results.length === 0) && (
              <Table.Row>
                <Table.Cell colSpan={9}>
                  <Flex
                    align="center"
                    justify="center"
                    py="6"
                    direction="column"
                    gap="2"
                  >
                    <Text size="2" color="gray">
                      No test results found
                    </Text>
                    <Text size="2" color="gray">
                      Run some regression tests to see results here
                    </Text>
                    <Button
                      color="gray"
                      highContrast
                      onClick={() =>
                        navigate("/automation-center/regression-sets")
                      }
                      className="mt-2"
                    >
                      Go to Regression Sets
                    </Button>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            )}
            {!isLoading &&
              sortedRegressionTests.map((result) => (
                <Table.Row key={result.id}>
                  <Table.Cell>
                    <Text size="1" className="font-mono">
                      ...{result?.id?.slice(-8)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="1" className="font-mono">
                      <Badge color="gray" variant="soft">
                        {
                          regressionSets?.find(
                            (set) => set.id === result?.regressionSetId
                          )?.name
                        }
                      </Badge>
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2" color="gray">
                      {formatRunMethodLabel(result.runMethod)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="1" className="font-mono">
                      {result?.results?.length}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Flex align="center" gap="2">
                      <Badge
                        color={
                          result?.results?.filter(
                            (r) => r.similarity > 0.8
                          ).length /
                            result?.results?.length >
                          0.8
                            ? "green"
                            : result?.results?.filter(
                                (r) => r.similarity > 0.8
                              ).length /
                                result?.results?.length >
                              0.6
                            ? "yellow"
                            : "red"
                        }
                        variant="soft"
                      >
                        {
                          result?.results?.filter(
                            (row) => row.similarity > 0.8
                          ).length
                        }
                        /{result?.results?.length} passed
                      </Badge>
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="1" className="font-mono">
                      {result?.results?.length
                        ? `Avg: ${(
                            (result.results.reduce(
                              (acc: number, curr: { similarity: number }) =>
                                acc +
                                (typeof curr.similarity === "number"
                                  ? curr.similarity
                                  : 0),
                              0
                            ) /
                              result.results.length) *
                            100
                          ).toFixed(1)}%`
                        : "N/A"}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    {new Date(result.createdAt).toLocaleDateString()}
                  </Table.Cell>

                  <Table.Cell>
                    <Button
                      variant="ghost"
                      size="1"
                      onClick={() => {
                        navigate(
                          `/automation-center/regression-tests/${result.id}`
                        );
                      }}
                    >
                      View results
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            {/* {!isLoading &&
              results
                ?.filter(
                  (result: any) =>
                    result.userComment?.comment
                      ?.toLowerCase()
                      .includes(searchPhrase.toLowerCase()) ||
                    result.agentComment?.comment
                      ?.toLowerCase()
                      .includes(searchPhrase.toLowerCase()) ||
                    result.aiResponse
                      ?.toLowerCase()
                      .includes(searchPhrase.toLowerCase())
                )
                .map((result: any) => (
                  <Table.Row key={result.id}>
                    <Table.Cell>
                      <Text size="1" className="font-mono">
                        {result.regressionTest?.id?.slice(0, 8)}...
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2" className="line-clamp-2">
                        {result.userComment?.comment || "N/A"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2" className="line-clamp-2">
                        {result.aiResponse || "N/A"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        color={
                          result.similarity > 0.8
                            ? "green"
                            : result.similarity > 0.6
                            ? "yellow"
                            : "red"
                        }
                        variant="soft"
                      >
                        {(result.similarity * 100).toFixed(1)}%
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {new Date(result.createdAt).toLocaleDateString()}
                    </Table.Cell>
                  </Table.Row>
                ))} */}
          </Table.Body>
        </Table.Root>
      </Flex>
    </Flex>
  );
}
